// Named service landing pages. Aggregators, not replacements. Each landing
// clusters existing services from /data/services.js under a durable named URL
// so search engines and AI answer engines can cite a single canonical page
// per category. Content lives here so it stays editable without touching the
// underlying service catalog.
export const SERVICE_LANDINGS = {
  "brand-strategy": {
    slug: "brand-strategy",
    key: "landingBrandStrategy",
    eyebrow: "Brand strategy",
    h1: "Decide who the business is for, before deciding how it looks.",
    seoTitle: "Brand Strategy for Service Businesses | Kostaja",
    metaDescription: "Positioning, audience, offer, and voice work for Canadian service businesses. Kostaja decides the upstream brand questions before any logo work begins.",
    sub: "Positioning, audience, offer, and voice work that decides how the business looks, reads, and gets talked about.",
    whoFor: "Owners of Canadian service businesses whose current brand feels dated, inconsistent, or unclear. Owners preparing to rebrand, relaunch, or add a second business line.",
    problem: "Most weak brand outcomes come from skipping strategy. A refresh without a strategy produces a nicer version of the same confusion. Owners spend on design before naming the audience, the offer, and the promise, and the design amplifies the gap.",
    builds: [
      "A written positioning statement in plain English",
      "A confirmed primary audience and how they decide",
      "The offer, the promise, and what the business will not do",
      "A tone-of-voice guide with examples",
      "A short brand system foundation (colour, typography, mark)",
      "A one-page scope for whatever ships next",
    ],
    deliverables: [
      "Positioning brief",
      "Audience map",
      "Voice guide with dos and don'ts",
      "Optional brand system foundation (logo, colour, type, layout)",
      "Handoff plan naming the next investment in order",
    ],
    process: [
      "30 minute working call to confirm fit",
      "Written intake covering business, audience, and current gaps",
      "Draft positioning, audience, and voice",
      "Working review to sharpen and confirm",
      "Final documents plus recommended sequence for the next step",
    ],
    services: ["strategy-call", "brand-audit", "brand-foundation-starter", "brand-foundation-core"],
    faqs: [
      { q: "What is brand strategy and how is it different from a logo?", a: "Brand strategy is the set of decisions about who the business serves, what it promises, and how it speaks. A logo is one output of those decisions. Skipping strategy is the largest single cause of weak brand outcomes in service businesses." },
      { q: "How long does a brand strategy engagement take?", a: "Two to six weeks for most Canadian service businesses. Shorter for audits. Longer when a full brand system foundation is bundled." },
      { q: "Do I need a rebrand or a refresh?", a: "Start with a brand audit. If positioning, audience, and voice are already clear, a refresh is enough. If any of those upstream pieces are unclear, a rebrand is the right investment." },
      { q: "How much does brand strategy cost in Canada?", a: "Well-scoped brand strategy for a service business usually costs one thousand to seven thousand Canadian dollars. Bundled brand systems that include strategy usually cost three thousand to twelve thousand." },
    ],
  },

  "website-design": {
    slug: "website-design",
    key: "landingWebsiteDesign",
    eyebrow: "Website design",
    h1: "A website that explains the business and makes the next step obvious.",
    seoTitle: "Website Design for Service Businesses | Kostaja",
    metaDescription: "Website design for Canadian service businesses. Clear service pages, visible proof, strong mobile contact paths, local search signals, and one next step on every page.",
    sub: "Websites that name the outcome, explain the offer, answer the objection, and end every page on one clear next step.",
    whoFor: "Canadian service businesses whose website is slower than the business, harder to read than a conversation, or missing a page for the things they actually sell.",
    problem: "Most service business websites fail on structure, not style. Beautiful sites with weak service pages leak buyers the moment the conversation gets specific. The homepage looks polished; the service pages read like drafts; the contact path is hidden.",
    builds: [
      "A working service page for every service the business sells",
      "A homepage that routes to the correct service in two clicks",
      "Copy written in plain English, buyer-first",
      "A contact path that opens cleanly from a phone",
      "Structured data for service, FAQ, and organization",
      "Performance and accessibility baseline built in",
    ],
    deliverables: [
      "Site architecture and navigation",
      "Copy for every service page and the homepage",
      "Design system, page templates, and interaction rules",
      "Development on a fast, low-maintenance stack",
      "Structured data and metadata",
      "Handoff, training, and post-launch support window",
    ],
    process: [
      "30 minute working call to confirm fit",
      "Site audit or content plan",
      "Wireframes and copy",
      "Visual design and prototype",
      "Build, review, and launch",
      "Two-week post-launch tuning window",
    ],
    services: ["website-audit", "website-foundation-starter", "website-core-build", "website-growth-build", "brand-and-website-launch-foundation"],
    faqs: [
      { q: "How much does a service business website cost in Canada?", a: "For most Canadian service businesses, a working website costs four to twelve thousand Canadian dollars for a growth build shape. Smaller starter builds cost two to five thousand. Larger connected brand-and-website launches cost ten to twenty-five thousand." },
      { q: "How long does a Kostaja website take?", a: "Four to twelve weeks for most projects. Audits and foundations ship faster. Larger connected projects take longer." },
      { q: "Do I need a service page for every service I sell?", a: "Yes, if you want that service to be found and cited by search engines and AI answer engines. A bullet on the homepage will not do the job." },
      { q: "What platform does Kostaja build on?", a: "Kostaja builds on the platform that fits the business. Most Canadian service businesses land on a fast, low-maintenance stack that they can maintain without a developer for routine updates." },
    ],
  },

  "social-media-marketing": {
    slug: "social-media-marketing",
    key: "landingSocialMedia",
    eyebrow: "Social media marketing",
    h1: "Show up consistently without turning social into a daily job.",
    seoTitle: "Social Media Marketing for Service Businesses | Kostaja",
    metaDescription: "Steady social media support for Canadian service businesses. A settled voice, a sustainable rhythm, and content tied to real services instead of trend chasing.",
    sub: "A steady social presence that supports familiarity, matches the brand, and does not turn into a trend-chasing treadmill.",
    whoFor: "Canadian service businesses that need a plain, consistent social presence without the burnout of daily performance content.",
    problem: "Most service businesses either post erratically or copy trends that do not fit their brand voice. Both produce the same result: activity without compound value. Social does not close deals for service businesses. It supports familiarity, which pays over months, not days.",
    builds: [
      "A settled brand voice with examples",
      "A content plan tied to the actual services",
      "A production rhythm the business can sustain",
      "Templates and layouts that keep the feed consistent",
      "A review, testimonial, and proof integration",
      "Reporting that names what is worth doing again",
    ],
    deliverables: [
      "Brand voice and post templates",
      "Content plan and posting calendar",
      "Two to four posts per week for most service businesses",
      "Simple monthly summary of what worked",
    ],
    process: [
      "30 minute working call to confirm fit",
      "Voice, audience, and offer confirmation",
      "First month of content shipped",
      "Ongoing cadence with monthly review",
    ],
    services: ["content-strategy-consultation", "content-and-social-audit", "content-planning-and-monthly-content-package", "monthly-content-package-starter", "monthly-content-package-growth"],
    faqs: [
      { q: "How often should a service business post on social media?", a: "Two consistent posts a week is enough for most Canadian service businesses. Consistency beats volume. A brand voice that stays steady month over month builds familiarity faster than a burst of posts that trails off." },
      { q: "Which platforms should a service business be on?", a: "One or two, run well. For most Canadian service businesses that means Instagram plus Facebook, or LinkedIn plus Instagram, depending on where the buyer actually looks. Spreading across five platforms produces five weak channels instead of two strong ones." },
      { q: "Do trends and viral content matter for service businesses?", a: "Rarely. Familiarity, trust, and repeatable calls to action move service business revenue. Trend chasing burns the operator without moving the numbers." },
      { q: "Can Kostaja run social media on our behalf?", a: "Yes. The monthly content packages ship two to four posts a week from a settled brand voice and include a short monthly summary of what is worth doing more of." },
    ],
  },

  "google-business-profile-local-seo": {
    slug: "google-business-profile-local-seo",
    key: "landingGbpLocalSeo",
    eyebrow: "Google Business Profile and local SEO",
    h1: "Show up when nearby customers are ready to choose.",
    seoTitle: "Google Business Profile & Local SEO for Service Businesses | Kostaja",
    metaDescription: "Kostaja improves Google Business Profile, website location signals, service information, review pathways, local pages, and search structure for Canadian service businesses.",
    sub: "The finished front door for local search. Complete, current, answered, and maintained.",
    whoFor: "Canadian service businesses that depend on nearby customers finding them through Google Search, Google Maps, or an AI answer engine that leans on those signals.",
    problem: "Most local service businesses have an unfinished Google Business Profile. Missing categories, outdated hours, no service list, no photos, and no answered reviews. This is the highest-leverage marketing gap most owners never close, and it costs quiet visits every day.",
    builds: [
      "A finished profile with correct categories and service list",
      "Service area, hours, and attributes that match reality",
      "Real photos of the business, team, or work",
      "A calm review pipeline with honest responses",
      "Consistent name, address, phone across directories",
      "A monthly maintenance rhythm the owner can actually run",
    ],
    deliverables: [
      "Google Business Profile audit and setup",
      "Category, service, service area, hours, and attribute configuration",
      "Photo curation and review response templates",
      "Local SEO consistency across major directories",
      "Monthly maintenance plan",
    ],
    process: [
      "30 minute working call to confirm fit",
      "Audit and gap list",
      "Optimization work",
      "Handoff with maintenance playbook",
      "Optional monthly maintenance support",
    ],
    services: ["google-business-profile-audit", "google-business-profile-optimization", "local-visibility-starter-kit", "google-business-profile-growth-support"],
    faqs: [
      { q: "What is a Google Business Profile and why does it matter?", a: "A Google Business Profile is the free listing that shows up on Google Search and Google Maps for a local business. For most Canadian service businesses it is doing more of the selling than the website is. A finished profile earns the click; the site converts it." },
      { q: "Can I rank number one on Google Maps?", a: "Nobody credible guarantees a specific position. Local ranking depends on relevance, distance, prominence, and behaviour signals Google does not publish. What a serious agency can do is finish the profile, keep it current, and give the business a real shot." },
      { q: "How often should the profile be updated?", a: "Two small updates a month. A fresh photo, a service update, a reply to a recent review. That rhythm is enough to look like a serious business from the outside." },
      { q: "Do reviews really move ranking and trust?", a: "Yes, when they are honest and specific. A mix of real customer language moves both human trust and AI answer engine confidence. A wall of five-star sentences without specifics does the opposite." },
    ],
  },

  "ai-receptionist": {
    slug: "ai-receptionist",
    key: "landingAiReceptionist",
    eyebrow: "AI receptionist and missed-call systems",
    h1: "Stop losing work between rings.",
    seoTitle: "AI Receptionist & Missed-Call Systems for Service Businesses | Kostaja",
    metaDescription: "Practical AI missed-call capture, after-hours response, and stalled-quote follow-up for Canadian service businesses. Honest about what it is. No pretending to be human.",
    sub: "Practical AI-supported communication that catches the calls and questions the business would otherwise lose. No gimmicks.",
    whoFor: "Canadian service businesses that lose work between rings. Solo operators, small teams, home services, automotive, medical clinics, salons, trades, and any service business where the phone matters and the owner cannot always answer it.",
    problem: "Every service business loses work between rings. Missed calls, after-hours enquiries, stalled quotes, and routine questions that never make it back to a human. Most owners try to fix this with a chatbot on the homepage. Wrong tool. The leak is on the phone, not the site.",
    builds: [
      "Missed-call auto-response that captures the caller and books a follow-up",
      "An after-hours question answerer inside the intake form or contact page",
      "Stalled-quote follow-up at two days, seven days, and fourteen days",
      "Clean human handoff when the conversation needs to be human",
      "Scripts and guardrails that match the brand voice",
      "Reporting that names what is being captured",
    ],
    deliverables: [
      "Missed-call and after-hours workflows",
      "AI script tuned to the brand voice",
      "Integration with existing phone, email, or booking system",
      "Guardrails and human handoff rules",
      "Monthly summary of captured demand",
    ],
    process: [
      "30 minute working call to confirm fit",
      "Audit of current call, email, and quote flow",
      "Workflow design and script tuning",
      "Live pilot with the owner in the loop",
      "Full rollout with monthly summary",
    ],
    services: ["custom-project", "strategy-call"],
    faqs: [
      { q: "Does an AI receptionist replace a real person?", a: "No. It catches the calls, questions, and follow-ups a small team cannot always answer, and hands off cleanly to a human when the conversation needs to be human. The goal is a phone that stops leaking work, not a phone that pretends to be a receptionist." },
      { q: "Will customers know they are talking to AI?", a: "Yes. Kostaja does not build AI systems that pretend to be human. The scripts are polite, plain, and honest. Customers respond well when the tool is useful and does not lie about what it is." },
      { q: "How much does an AI receptionist cost?", a: "For most Canadian service businesses, a practical AI missed-call and after-hours setup is a custom project. Setup fees usually fall between one thousand and four thousand Canadian dollars, plus a small monthly usage fee. Costs are named in a written scope before work begins." },
      { q: "What is the fastest first move for AI in a service business?", a: "The missed-call system. Every service business loses work between rings. Fixing that leak is often the highest-return AI investment available and pays back in weeks, not months." },
    ],
  },

  "print-sales-materials": {
    slug: "print-sales-materials",
    key: "landingPrintSales",
    eyebrow: "Print and sales materials",
    h1: "Make the offer easier to understand and the conversation easier to advance.",
    seoTitle: "Print & Sales Materials for Service Businesses | Kostaja",
    metaDescription: "Business cards, one-page sales sheets, estimate leave-behinds, and sales decks for Canadian service businesses. Disciplined, specific, and built to support real meetings.",
    sub: "Cards, sheets, and one-pagers that support real sales meetings. Small, disciplined, disproportionately effective.",
    whoFor: "Canadian service businesses that meet customers in person, leave estimates behind, present quotes, or work in industries where a physical leave-behind still moves the decision.",
    problem: "Most printed and PDF sales materials try to be everything. Every service. Every award. Every testimonial. The reader gets more paper than they can carry and none of the specific answer they came for. The result is decoration that gets thrown away.",
    builds: [
      "Business cards that support real handshakes",
      "One-page sales sheets that answer one specific question",
      "Leave-behinds for estimates and site visits",
      "Sales decks written for the reader after the meeting, not during it",
      "Print production spec and vendor coordination",
      "Templates the business can reuse without a designer",
    ],
    deliverables: [
      "Business card design and print spec",
      "One-page sales sheet or leave-behind",
      "Optional sales deck or pitch deck refinement",
      "Print production coordination if requested",
    ],
    process: [
      "30 minute working call to confirm fit",
      "Audience and use-case confirmation",
      "Copy and design",
      "Production spec or handoff to preferred print vendor",
    ],
    services: ["business-card-design", "one-page-sales-sheet", "pitch-deck-refinement", "print-collateral-package"],
    faqs: [
      { q: "Do printed materials still matter for a modern service business?", a: "Yes, in specific situations. In-person estimates, site visits, real estate open houses, medical consultations, and industry events all reward businesses that leave a piece of well-produced paper behind. It reads as seriously as the business behind it." },
      { q: "What is the best first print piece to invest in?", a: "For most service businesses, a well-made business card and one specific one-page sales sheet. That combination covers ninety percent of use cases and stops the business from leaning on a generic brochure that answers no question well." },
      { q: "How much do print materials cost through Kostaja?", a: "Design for business cards, one-pagers, and short leave-behinds usually falls between three hundred and two thousand Canadian dollars per piece. Print production is billed at cost through the client's preferred vendor or a Kostaja-coordinated vendor." },
      { q: "Does Kostaja handle the printing itself?", a: "Kostaja delivers press-ready files and can coordinate production with trusted Canadian print vendors on request. Owners who already have a printer they trust keep working with them." },
    ],
  },

  "marketing-systems": {
    slug: "marketing-systems",
    key: "landingMarketingSystems",
    eyebrow: "Marketing systems",
    h1: "Connect the pieces so the owner is not managing six vendors.",
    seoTitle: "Marketing Systems for Service Businesses | Kostaja",
    metaDescription: "Brand, website, local search, social, print, and AI-supported communication connected into one working system for Canadian service businesses. One studio. One accountable team.",
    sub: "Connect brand, website, local search, social, print, and AI-supported communication into one working system, not six invoices.",
    whoFor: "Canadian service businesses that already own the pieces but need them connected. Owners who have paid for a website, a logo, a social calendar, and a listing, and still feel the marketing is scattered.",
    problem: "Disconnected marketing produces disconnected results. A rebrand that never touches the website. A website that never syncs to the Google Business Profile. A social calendar with no intake path. An AI follow-up on a business with no brand foundation. Every piece asks the customer to do more of the work of choosing.",
    builds: [
      "A working brand system that every future touchpoint inherits",
      "A website that catches attention from every channel",
      "A finished Google Business Profile with a real maintenance rhythm",
      "A social presence built for familiarity, not performance",
      "AI-supported communication that catches missed demand",
      "Print materials that support the parts of the business that meet in person",
      "One team with commercial judgment owning the strategy, the writing, and the delivery",
    ],
    deliverables: [
      "A written system map showing how the pieces work together",
      "One-page scope for the full engagement",
      "Milestone plan with clear deliverables at each step",
      "Post-launch review and adjustment window",
    ],
    process: [
      "30 minute working call to confirm fit",
      "Audit of every existing piece",
      "System map and recommended sequence",
      "Milestone build",
      "Post-launch review and support",
    ],
    services: ["strategy-call", "marketing-consultation-package", "brand-and-website-launch-foundation", "brand-and-website-growth-system", "complete-business-presence-system", "custom-project"],
    faqs: [
      { q: "What is a marketing system?", a: "A marketing system is the connected set of pieces that make a service business easier to trust, easier to find, and easier to choose. Brand, website, local search, social presence, reviews, print, and AI-supported communication, all reinforcing each other under one strategy." },
      { q: "How long does a full marketing system take to build?", a: "Six to sixteen weeks for most Canadian service businesses. Owners who already own strong pieces move faster. Owners rebuilding from scratch take longer but move further." },
      { q: "How much does a marketing system cost in Canada?", a: "Full connected engagements for Canadian service businesses usually cost fifteen to sixty thousand Canadian dollars, priced in a written scope with milestone deposits. Smaller partial systems cost less and can be sequenced over time." },
      { q: "Do I have to build every piece at once?", a: "No. Most Kostaja engagements start with one or two pieces and ladder into a fuller system over time. The value is the sequence." },
    ],
  },

  // ---------------- Four macro hubs (Brand / Websites / Marketing / Ongoing Partner) ----------------
  "brand": {
    slug: "brand",
    key: "landingBrand",
    eyebrow: "Branding",
    h1: "Make the business look as serious as it is.",
    seoTitle: "Branding for Service Businesses | Brand Strategy & Identity | Kostaja",
    metaDescription: "Kostaja builds brand systems for service businesses that need to look more recognizable, credible, and consistent across websites, social, signs, print, and sales materials.",
    sub: "Build a business people recognize, understand, and trust.",
    whoFor: "Owners whose company has outgrown its current identity. Founders launching something new. Teams whose brand has drifted across social, print, and web to the point where the business no longer looks like itself.",
    problem: "A logo is not a brand. A brand is the connected set of decisions and assets that keep a business looking, sounding, and behaving like itself across every place a customer meets it. Most service businesses invest in the visible parts before deciding the strategic ones. The result is a nicer version of the same confusion.",
    builds: [
      "Positioning: who this is for, what it promises, and what it stands against",
      "A visual identity system: mark, typography, colour, layout rules",
      "Voice guide with real examples",
      "Applications for social, print, web, and sales materials",
      "Guidelines so the brand stays consistent when other people work on it",
    ],
    deliverables: [
      "Brand strategy documented in plain English",
      "Logo system with lockups and clear-space rules",
      "Colour and typography with accessibility baseline",
      "Voice and messaging examples",
      "Social, print, and sales-material templates",
      "Written brand guidelines",
    ],
    process: [
      "Working call to confirm fit and scope",
      "Written intake covering business, audience, and current gaps",
      "Positioning and voice work",
      "Visual system design and review",
      "Applications, guidelines, and handoff",
    ],
    services: ["strategy-call", "brand-audit", "brand-foundation-starter", "brand-foundation-core", "brand-foundation-complete"],
    faqs: [
      { q: "How much does branding cost for a Canadian service business?", a: "Well-scoped brand systems for Canadian service businesses usually cost three to twelve thousand Canadian dollars. Larger rebrands with applications cost more. A brand audit or strategy call is the fastest way to find out where your business sits." },
      { q: "Do I need a rebrand, a refresh, or just an audit?", a: "Start with an audit. If positioning, audience, and voice are clear and only the visual system feels tired, a refresh is enough. If the upstream decisions have shifted, a full rebrand is the right investment." },
      { q: "How long does a brand engagement take?", a: "Two to eight weeks for most Canadian service businesses, depending on scope. Kostaja provides a written timeline in the scope before work starts." },
    ],
  },

  "websites": {
    slug: "websites",
    key: "landingWebsites",
    eyebrow: "Website services",
    h1: "Build a website people understand and act on.",
    seoTitle: "Website Services for Service Businesses | Kostaja",
    metaDescription: "Website design, redesign, audits, and care for service businesses. Kostaja builds sites structured around real buying decisions, mobile use, local search, speed, and lead capture.",
    sub: "A website that looks the part, explains the business, and earns the next step.",
    whoFor: "Owners whose website is slower than the business it represents. Founders launching a real operation and refusing to launch with a template. Teams whose current site has traffic but no inquiries.",
    problem: "Most service business sites fail on structure, not style. Beautiful sites with weak service pages leak buyers the moment the conversation gets specific. The homepage looks polished, the service pages read like drafts, the contact path is hidden. A working site is a sales tool, not a brochure.",
    builds: [
      "Site strategy and information architecture",
      "One dedicated service page per real service the business sells",
      "Copy written buyer-first, in plain English",
      "Design system, page templates, and interaction rules",
      "Fast, mobile-first build on a low-maintenance stack",
      "Structured data, accessibility baseline, and performance tuning",
    ],
    deliverables: [
      "Sitemap and navigation",
      "Copy for homepage, service pages, and supporting pages",
      "Full visual design system",
      "Development and QA on a modern stack",
      "Structured data, Open Graph, and metadata",
      "Handoff, training, and a post-launch support window",
    ],
    process: [
      "Working call to confirm fit and scope",
      "Content plan and copy first",
      "Wireframes and visual design",
      "Build, review, launch",
      "Two-week post-launch tuning window",
    ],
    services: ["website-audit", "website-foundation-starter", "website-growth-build", "website-system-complete", "brand-and-website-launch-foundation", "brand-and-website-growth-system"],
    faqs: [
      { q: "How much does a service business website cost in Canada?", a: "For most Canadian service businesses, a working website costs four to twelve thousand Canadian dollars. Smaller starter builds cost two to five thousand. Connected brand-and-website engagements cost ten to twenty-five thousand." },
      { q: "How long does a website take to build?", a: "A tightly scoped service business website usually takes four to ten weeks. A brand plus website takes six to fourteen weeks." },
      { q: "What platform do you build on?", a: "Kostaja recommends the platform that fits the business, the budget, and the maintenance appetite. Most sites are built on a fast, low-maintenance modern stack the operator can update without a developer." },
    ],
  },

  "marketing": {
    slug: "marketing",
    key: "landingMarketing",
    eyebrow: "Marketing",
    h1: "Give the right customers more reasons to notice, trust, and act.",
    seoTitle: "Marketing Services for Service Businesses | Kostaja",
    metaDescription: "Kostaja helps service businesses connect their website, local search, content, social, print, and sales materials around a clear commercial goal instead of running disconnected tactics.",
    sub: "Put the business in front of the right people and give them a reason to act.",
    whoFor: "Owners whose brand and website are already working, and now need consistent visibility, content, and campaign support. Businesses whose Google Business Profile, local search, and social presence do not match the quality of the operation.",
    problem: "Marketing without a foundation is expensive. A steady social feed cannot rescue a weak brand. Local ads cannot rescue an unfinished Google Business Profile. Kostaja builds the pieces that make marketing work, then runs the rhythm that keeps them producing.",
    builds: [
      "Google Business Profile completion and ongoing maintenance",
      "Local SEO and city-level visibility",
      "Social content and posting rhythm",
      "Content and editorial for search and AI answer engines",
      "Print and sales materials that support real meetings",
      "Campaign creative and launch support",
    ],
    deliverables: [
      "Google Business Profile audit and rebuild",
      "Local SEO structural work",
      "Monthly content plans and creative",
      "Ad creative where applicable",
      "Print and sales materials",
      "Reporting and adjustments",
    ],
    process: [
      "Working call to confirm fit and priorities",
      "Audit of current presence and gaps",
      "Written scope for the first quarter",
      "Ship the first pieces and set the rhythm",
      "Monthly review and adjustment",
    ],
    services: ["local-seo-snapshot", "google-business-profile-optimization", "google-business-profile-growth-support", "marketing-support-retainer-starter", "marketing-support-growth", "local-visibility-starter-kit"],
    faqs: [
      { q: "How much does marketing cost for a Canadian service business?", a: "Small monthly marketing engagements start around eight hundred Canadian dollars per month. Fuller connected support runs one thousand five hundred to five thousand per month depending on scope. One-time fixed-scope services (like a GBP rebuild) fall outside the monthly rhythm." },
      { q: "Do you handle paid advertising?", a: "Kostaja builds ad creative and landing pages, and coordinates with vetted paid-media specialists on ongoing campaigns. Kostaja does not run large paid budgets in-house." },
      { q: "How is this different from a general marketing agency?", a: "Kostaja focuses on service businesses and on the connected system (brand, website, local search, social, print) rather than on siloed tactics. The advantage is one accountable team and a coherent public presence." },
    ],
  },

  "ongoing-partner": {
    slug: "ongoing-partner",
    key: "landingOngoingPartner",
    eyebrow: "Ongoing marketing partner",
    h1: "Senior marketing and creative support without building the department in-house.",
    seoTitle: "Ongoing Marketing Partner for Service Businesses | Kostaja",
    metaDescription: "Fractional senior marketing and creative support for Canadian service businesses that need an accountable partner owning brand, website, and marketing rhythm month to month.",
    sub: "Senior marketing and creative support without building the whole department in-house.",
    whoFor: "Businesses that need consistent senior-level marketing and creative support but cannot justify a full in-house team. Growing operations whose marketing has become a bottleneck for the founder.",
    problem: "Most growing businesses do not need to hire five specialists. They need one accountable partner who owns the brand, website, and marketing rhythm and coordinates specialists when the work calls for it. Fractional support that reads senior, ships on time, and stays inside a written scope.",
    builds: [
      "Fractional marketing leadership",
      "Brand stewardship and consistency across new work",
      "Ongoing creative and design",
      "Website updates and improvements",
      "Content and campaign development",
      "Coordination with specialists (paid media, video, PR) when needed",
    ],
    deliverables: [
      "Written monthly scope of priorities and deliverables",
      "Named point of contact from studio leadership",
      "Regular working reviews",
      "Quarterly planning conversations",
      "Transparent reporting",
    ],
    process: [
      "Working call to confirm fit and identify the highest-leverage first priorities",
      "Written scope, monthly cadence, and pricing",
      "Kickoff, then a steady monthly rhythm",
      "Quarterly reviews to reset priorities",
    ],
    services: ["marketing-support-retainer-starter", "marketing-support-growth", "google-business-profile-growth-support", "google-business-profile-growth-support-plus"],
    faqs: [
      { q: "How much does an ongoing partner engagement cost?", a: "Fractional ongoing engagements for Canadian service businesses usually run one thousand five hundred to six thousand Canadian dollars per month, priced against a written monthly scope. Larger fractional-CMO shape engagements run higher." },
      { q: "What is the minimum commitment?", a: "Three months. Enough time to ship real work and see whether the rhythm is producing. Month-to-month after that." },
      { q: "Do you replace an in-house marketing hire?", a: "For many small and mid-sized service businesses, yes. For larger businesses with real volume and complexity, Kostaja often works alongside an internal marketing lead rather than replacing one." },
    ],
  },
};
